import { ArrowRight, LockKeyOpen, ShieldCheck } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

import { StoryCard } from "@/components/StoryCard";
import { Button } from "@/components/ui/button";
import { usePageSeo } from "@/hooks/usePageSeo";
import { ENTITY_PAGE_LIST } from "@/utils/entityPages";
import { getNewsroomStories } from "@/utils/newsroomCollections";

export const InvestigationsLandingPage = ({ feed }) => {
  const stories = getNewsroomStories(feed, "investigations", 6);

  usePageSeo({
    canonicalPath: "/investigations",
    description: "Content Watch HQ investigations hub featuring accountability reporting, entity watch pages, and the secure whistleblower portal.",
    title: "Investigations | Content Watch HQ",
  });

  return (
    <main className="page-shell mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="investigations-page">
      <section className="grid gap-8 border border-[#D9DCE1] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] lg:grid-cols-[minmax(0,1.1fr)_360px] lg:p-8" data-testid="investigations-hero">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]" data-testid="investigations-overline">Investigations desk</p>
          <h1 className="mt-3 font-[Bebas_Neue] text-5xl uppercase tracking-[0.04em] text-[#0A0A0A] sm:text-6xl" data-testid="investigations-title">Accountability coverage with a locked-down source portal</h1>
          <p className="mt-4 max-w-3xl text-base leading-8 text-slate-700" data-testid="investigations-description">This desk is built for whistleblowers, records-driven tips, public corruption leads, and follow-up reporting that needs more protection than a standard contact form.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild className="rounded-none bg-[#1338BE] px-5 py-5 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#102F9B]" data-testid="investigations-open-portal-button">
              <Link to="/submit-a-tip">
                Open Secure Portal
                <ArrowRight size={16} weight="bold" />
              </Link>
            </Button>
            <Button asChild className="rounded-none border border-[#0A0A0A] bg-transparent px-5 py-5 text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid="investigations-breaking-button" variant="ghost">
              <Link to="/breaking-news">Back to Breaking News</Link>
            </Button>
          </div>
        </div>

        <div className="space-y-4" data-testid="investigations-right-rail">
          <div className="border border-[#D9DCE1] bg-[#0A0A0A] p-5 text-white" data-testid="investigations-source-protection-card">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#D21404]">
              <ShieldCheck size={16} weight="fill" />
              Source protection
            </p>
            <p className="mt-3 text-sm leading-7 text-slate-200">The portal now issues a tracking ID and case key, supports follow-up messaging, and keeps newsroom replies inside the same protected thread.</p>
          </div>
          <div className="border border-[#D9DCE1] bg-[#F8F9FA] p-5" data-testid="investigations-entity-watch-card">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#1338BE]">
              <LockKeyOpen size={16} weight="fill" />
              Entity watch
            </p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="investigations-entity-links">
              {ENTITY_PAGE_LIST.slice(0, 5).map((entity) => (
                <Link key={entity.key} className="border border-[#D9DCE1] bg-white px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-700 transition-colors hover:border-[#1338BE] hover:text-[#1338BE]" data-testid={`investigations-entity-link-${entity.key}`} to={entity.route}>
                  {entity.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mt-10 grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px]" data-testid="investigations-body-grid">
        <div>
          <div className="mb-6">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]" data-testid="investigations-coverage-overline">Latest investigations</p>
            <h2 className="mt-3 font-[Bebas_Neue] text-4xl uppercase tracking-[0.04em] text-[#0A0A0A]" data-testid="investigations-coverage-title">Published accountability reporting</h2>
          </div>
          <div className="news-card-grid" data-testid="investigations-story-grid">
            {stories.length ? stories.map((story) => <StoryCard key={story.id || story.slug} story={story} />) : (
              <div className="border border-[#D9DCE1] bg-white p-8 md:col-span-2 xl:col-span-3" data-testid="investigations-empty-state">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">Desk is live</p>
                <p className="mt-3 text-base leading-8 text-slate-700">Investigation-tagged stories will appear here as the newsroom publishes them. The whistleblower and entity SEO infrastructure is already live.</p>
              </div>
            )}
          </div>
        </div>

        <aside className="space-y-4" data-testid="investigations-briefing-rail">
          <div className="border border-[#D9DCE1] bg-white p-5" data-testid="investigations-briefing-panel">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#1338BE]">How it works</p>
            <ol className="mt-4 space-y-3 text-sm leading-7 text-slate-700" data-testid="investigations-briefing-steps">
              <li>1. Submit documents, names, dates, or screenshots through the secure portal.</li>
              <li>2. Save your tracking ID and case key right away.</li>
              <li>3. Return anytime to check status or send follow-up evidence.</li>
            </ol>
          </div>
        </aside>
      </section>
    </main>
  );
};